import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Download, Flame, MessageCircle, Search, SlidersHorizontal, ThumbsUp, TrendingUp, X, MoreVertical, Pencil, Trash2, Filter } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import DashboardLayout from '../../../components/DashboardLayout';
import CommentModal from '../../../components/common/CommentModal';
import {
  Admin_GetPostAttachmentSasUrl,
  Admin_GetTrendingHashtags,
  Admin_DeletePost,
  GetTrendingByHashtag,
  Admin_PostComment,
  Admin_TogglePostLike,
  Admin_ViewPosts,
} from '../../../constants/adminData';
import { Attachment, PostItem, TrendingHashtag } from '../../../types/admin';
import { getSensitiveData } from '../../../utils/secureStorage';
import { useAppSelector } from '../../../store';
import { getPhotoSasUrl } from '../../../services/api/imageService';
import styles from './createPost.module.css';
import { useMenuLabels } from '@/hooks/useMenuLabels';
import { formatDate } from '@/utils/useUtil';
import Toast from '../../../components/Toast/Toast';
import PhotoImage from '@/components/common/PhotoImage';

type FeedFilter = 'recent' | 'liked' | 'oldest';
type VisibilityFilter = 'all' | 'public' | 'network' | 'onlyMe';

const POSTS_PAGE_SIZE = 10;
const CLIENT_FILTER_FETCH_SIZE = 10000;

const filterOptions: { value: FeedFilter; label: string }[] = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'liked', label: 'Most Liked' },
  { value: 'oldest', label: 'Oldest' },
];

const visibilityOptions: { value: VisibilityFilter; label: string }[] = [
  { value: 'all', label: 'All visibility' },
  { value: 'public', label: 'Public' },
  { value: 'network', label: 'Your Network' },
  { value: 'onlyMe', label: 'Only Me' },
];

type LoadedAttachment = {
  attachment: Attachment;
  url: string | null;
  status: 'loading' | 'loaded' | 'error';
  didRetry: boolean;
};

type ProfileImageEntry = {
  url: string | null;
  status: 'loading' | 'loaded' | 'error';
};

type ProfileImageMap = Record<string, ProfileImageEntry>;

const attachmentUrlCache = new Map<number, string>();
const attachmentPromiseCache = new Map<number, Promise<string>>();
const profileImageUrlCache = new Map<string, string>();
const profileImagePromiseCache = new Map<string, Promise<string>>();

const extractSasUrl = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    const maybeFileUrl = (value as { fileUrl?: unknown }).fileUrl;
    if (typeof maybeFileUrl === 'string' && maybeFileUrl) return maybeFileUrl;
  }
  return null;
};

const getValidImageId = (value?: number | string | null) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const getAttachmentId = (attachment: Attachment) => {
  return getValidImageId(attachment.attachmentId);
};

// Blob paths returned by the post API are often private. Only use a URL directly
// when it is already signed (or is a local preview); otherwise request a SAS URL.
const getAttachmentUrl = (attachment: Attachment) => {
  const url = attachment.fileUrl || attachment.tempFileUrl;
  if (!url) return null;

  return url.startsWith('blob:') || url.startsWith('data:') || /[?&]sig=/i.test(url)
    ? url
    : null;
};

const isImageAttachment = (attachment: Attachment) => {
  const type = attachment.fileType || attachment.contentType || '';
  return type.toLowerCase().includes('image');
};

const fetchAttachmentUrl = async (attachmentId: number, forceRefresh = false) => {
  if (forceRefresh) {
    attachmentUrlCache.delete(attachmentId);
    attachmentPromiseCache.delete(attachmentId);
  }

  const cachedUrl = attachmentUrlCache.get(attachmentId);
  if (cachedUrl) return cachedUrl;

  const cachedPromise = attachmentPromiseCache.get(attachmentId);
  if (cachedPromise) return cachedPromise;

  const promise = Admin_GetPostAttachmentSasUrl(attachmentId)
    .then((response) => {
      const url = extractSasUrl(response?.data?.data) ?? extractSasUrl(response?.data?.fileUrl);
      if (!url) {
        throw new Error('Attachment SAS URL missing');
      }
      attachmentUrlCache.set(attachmentId, url);
      attachmentPromiseCache.delete(attachmentId);
      return url;
    })
    .catch((error) => {
      attachmentPromiseCache.delete(attachmentId);
      throw error;
    });

  attachmentPromiseCache.set(attachmentId, promise);
  return promise;
};

const fetchProfileImage = async (profilePhotoId: number) => {
  // Keep this page aligned with create-post image resolution flow.
  const userPhotoUrl = await getPhotoSasUrl(profilePhotoId);

  if (userPhotoUrl) {
    return userPhotoUrl;
  }

  // Fallback for environments where profile photo ids are served by attachment endpoint.
  const attachmentResponse = await Admin_GetPostAttachmentSasUrl(profilePhotoId);
  const attachmentUrl =
    extractSasUrl(attachmentResponse?.data?.data) ??
    extractSasUrl(attachmentResponse?.data?.fileUrl);

  if (!attachmentUrl) {
    throw new Error('Profile image URL missing');
  }

  return attachmentUrl;
};

function formatTimeAgo(dateStr?: string) {
  if (!dateStr) return '';

  const time = new Date(dateStr).getTime();
  if (Number.isNaN(time)) return '';

  const diff = Math.max(0, Date.now() - time);
  const mins = Math.floor(diff / 60000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  return `${Math.floor(hrs / 24)}d ago`;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase() || 'U';
}

function Header() {
  const labels = useMenuLabels();
 const viewPostLabel = labels.viewPosts;
  return (
    <div className={styles.viewHeader}>
      <div>
        <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">Latest updates from your network</p>
      </div>
    </div>
  );
}

function FilterDropdown({
  value,
  onChange,
  onAfterChange,
  compact = false,
}: {
  value: FeedFilter;
  onChange: (value: FeedFilter) => void;
  onAfterChange?: () => void;
  compact?: boolean;
}) {
  return (
    <label className={compact ? styles.sheetSelect : styles.feedSort}>
      <SlidersHorizontal className={styles.actionIcon} />
      <select
        aria-label="Filter posts"
        value={value}
        onChange={(event) => {
          onChange(event.target.value as FeedFilter);
          onAfterChange?.();
        }}
      >
        {filterOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function VisibilityDropdown({
  value,
  onChange,
  compact = false,
}: {
  value: VisibilityFilter;
  onChange: (value: VisibilityFilter) => void;
  compact?: boolean;
}) {
  return (
  <label className={compact ? styles.sheetSelect : styles.feedSort}>
      <Filter className={styles.actionIcon} aria-hidden="true" />
      <select
        aria-label="Filter posts by visibility"
        value={value}
        onChange={(event) => onChange(event.target.value as VisibilityFilter)}
      >
        {visibilityOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TrendsPanel({
  inSheet = false,
  hashtags,
  isLoading,
  onSelectHashtag,
}: {
  inSheet?: boolean;
  hashtags: TrendingHashtag[];
  isLoading: boolean;
  onSelectHashtag?: (tag: string) => void;
}) {

    // Declared memoized subset to strictly limit dashboard listing to the top 10 trends
    const topTenHashtags = useMemo(() => {
      return Array.isArray(hashtags) ? hashtags.slice(0, 10) : [];
    }, [hashtags]);

  return (
    <aside className={inSheet ? styles.trendsSheetPanel : styles.trendsPanel}>
      <div className={styles.trendsHeader}>
        <Flame className={styles.actionIcon} />
        <h2>Trending</h2>
      </div>
      <div className={styles.trendList}>
        {isLoading ? (
          <span className={styles.trendItem}>Loading trends...</span>
        ) : topTenHashtags.length === 0 ? (
          <span className={styles.trendItem}>Coming soon</span>
        ) : (
          topTenHashtags.map((trend) => (
            <button
              key={trend.tag}
              type="button"
              className={styles.trendItem}
              onClick={() => onSelectHashtag?.(trend.tag)}
            >
              <span>{trend.tag}</span>
              <small>{trend.postCount} posts</small>
            </button>
          ))
        )}
      </div>
    </aside>
  );
}

function BottomSheet({
  title,
  isOpen,
  onClose,
  children,
}: {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!isOpen) return null;

  return (
    <div className={styles.sheetBackdrop} role="presentation" onClick={onClose}>
      <section
        className={styles.bottomSheet}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.sheetHeader}>
          <h2>{title}</h2>
          <button type="button" className={styles.sheetClose} onClick={onClose} aria-label="Close">
            <X className={styles.actionIcon} />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}

function LikeButton({
  count,
  isLiked,
  onToggle,
}: {
  count: number;
  isLiked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.feedActionButton} ${isLiked ? styles.feedActionButtonActive : ''}`}
      onClick={onToggle}
      aria-pressed={isLiked}
    >
      <ThumbsUp className={styles.actionIcon} />
      <span>{count} Like</span>
    </button>
  );
}

function AttachmentGallery({
  postId,
  attachments,
}: {
  postId: number;
  attachments?: Attachment[] | null;
}) {
  const sortedAttachments = useMemo(
    () =>
      [...(attachments ?? [])]
        .filter((attachment) => getAttachmentId(attachment) !== null)
        .sort((first, second) => (first.displayOrder ?? 0) - (second.displayOrder ?? 0)),
    [attachments]
  );
  const attachmentKey = useMemo(
    () => sortedAttachments.map((attachment) => getAttachmentId(attachment)).join('|'),
    [sortedAttachments]
  );
  const [loadedAttachments, setLoadedAttachments] = useState<LoadedAttachment[]>(() =>
    sortedAttachments.map((attachment) => ({
      attachment,
      url: getAttachmentUrl(attachment) ?? attachmentUrlCache.get(getAttachmentId(attachment)!) ?? null,
      status: getAttachmentUrl(attachment) || attachmentUrlCache.has(getAttachmentId(attachment)!) ? 'loaded' : 'loading',
      didRetry: false,
    }))
  );

  useEffect(() => {
    let isMounted = true;

    setLoadedAttachments(
      sortedAttachments.map((attachment) => ({
        attachment,
        url: getAttachmentUrl(attachment) ?? attachmentUrlCache.get(getAttachmentId(attachment)!) ?? null,
        status: getAttachmentUrl(attachment) || attachmentUrlCache.has(getAttachmentId(attachment)!) ? 'loaded' : 'loading',
        didRetry: false,
      }))
    );

    sortedAttachments.forEach((attachment) => {
      const attachmentId = getAttachmentId(attachment);
      if (!attachmentId || getAttachmentUrl(attachment) || attachmentUrlCache.has(attachmentId)) return;

      fetchAttachmentUrl(attachmentId)
        .then((url) => {
          if (!isMounted) return;
          setLoadedAttachments((current) =>
            current.map((item) =>
              getAttachmentId(item.attachment) === attachmentId
                ? { ...item, url, status: 'loaded' }
                : item
            )
          );
        })
        .catch((error) => {
          console.error('Error loading post attachment:', error);
          if (!isMounted) return;
          setLoadedAttachments((current) =>
            current.map((item) =>
              getAttachmentId(item.attachment) === attachmentId
                ? { ...item, status: 'error' }
                : item
            )
          );
        });
    });

    return () => {
      isMounted = false;
    };
  }, [postId, attachmentKey]);

  if (sortedAttachments.length === 0) return null;

  const hasOnlyDocuments = loadedAttachments.every(
    ({ attachment }) => !isImageAttachment(attachment)
  );

  if (hasOnlyDocuments) {
    return (
      <div className={styles.fileList}>
        {loadedAttachments.map(({ attachment, url, status }) => (
          <div key={getAttachmentId(attachment) ?? attachment.displayOrder} className={styles.fileItem}>
            <span>{attachment.fileName || 'Attachment'}</span>
            {status === 'loading' ? (
              <span className={styles.fileDownloadStatus}>Loading…</span>
            ) : status === 'error' || !url ? (
              <span className={styles.fileDownloadStatus}>Unavailable</span>
            ) : (
              <a
                className={styles.downloadFile}
                href={url}
                download={attachment.fileName || undefined}
                aria-label={`Download ${attachment.fileName || 'attachment'}`}
                title="Download attachment"
              >
                <Download className={styles.actionIcon} aria-hidden="true" />
              </a>
            )}
          </div>
        ))}
      </div>
    );
  }

  const handleAttachmentImageError = (attachment: Attachment) => {
    const attachmentId = getAttachmentId(attachment);
    if (!attachmentId) return;

    const currentItem = loadedAttachments.find(
      (item) => getAttachmentId(item.attachment) === attachmentId
    );

    if (currentItem?.didRetry) {
      setLoadedAttachments((current) =>
        current.map((item) =>
          getAttachmentId(item.attachment) === attachmentId
            ? { ...item, status: 'error' }
            : item
        )
      );
      return;
    }

    setLoadedAttachments((current) =>
      current.map((item) =>
        getAttachmentId(item.attachment) === attachmentId
          ? { ...item, status: 'loading', didRetry: true }
          : item
      )
    );

    fetchAttachmentUrl(attachmentId, true)
      .then((url) => {
        setLoadedAttachments((current) =>
          current.map((item) =>
            getAttachmentId(item.attachment) === attachmentId
              ? { ...item, url, status: 'loaded' }
              : item
          )
        );
      })
      .catch((error) => {
        console.error('Error retrying post attachment:', error);
        setLoadedAttachments((current) =>
          current.map((item) =>
            getAttachmentId(item.attachment) === attachmentId
              ? { ...item, status: 'error' }
              : item
          )
        );
      });
  };

  return (
    <div
      className={`${styles.attachmentGrid} ${loadedAttachments.length === 1 ? styles.attachmentGridSingle : ''
        } ${loadedAttachments.length === 2 ? styles.attachmentGridTwo : ''}`}
    >
      {loadedAttachments.map(({ attachment, url, status }) => (
        <div key={getAttachmentId(attachment) ?? attachment.displayOrder} className={styles.attachmentTile}>
          {status === 'loading' ? (
            <div className={styles.attachmentSkeleton} />
          ) : status === 'error' || !url ? (
            <div className={styles.attachmentFallback}>Attachment unavailable</div>
          ) : isImageAttachment(attachment) ? (
            <img
              src={url}
              alt={attachment.fileName || 'Post attachment'}
              loading="lazy"
              onError={() => handleAttachmentImageError(attachment)}
            />
          ) : (
            <a
              className={styles.attachmentFile}
              href={url}
              download={attachment.fileName || undefined}
              aria-label={`Download ${attachment.fileName || 'attachment'}`}
            >
              <Download className={styles.actionIcon} aria-hidden="true" />
              <span>{attachment.fileName || 'Download attachment'}</span>
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
const renderContentBlocks = (post: PostItem) => {
  if (!post.contentBlocks?.length) {
    return (
      <>
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(post.content ?? ''),
          }}
        />
        <AttachmentGallery postId={post.postId} attachments={post.attachments} />
      </>
    );
  }

  const attachmentMap = new Map(
    (post.attachments || []).map((a) => [
      Number(a.attachmentId),
      a,
    ])
  );

  const blocks = [...post.contentBlocks].sort(
    (a, b) =>
      (a.displayOrder || 0) -
      (b.displayOrder || 0)
  );
  const attachmentIdsInBlocks = new Set(
    blocks
      .filter((block) => ['IMAGE', 'FILE'].includes(block.blockType?.toUpperCase()))
      .map((block) => Number(block.attachmentId))
  );
  const attachmentsOutsideBlocks = (post.attachments || []).filter(
    (attachment) => !attachmentIdsInBlocks.has(Number(attachment.attachmentId))
  );

  return (
    <>
      {blocks.map((block, index) => {
        const type = block.blockType?.toUpperCase();

        if (type === 'TEXT') {
          return (
            <p key={index}>
              {block.textContent}
            </p>
          );
        }

        if (type === 'LINK') {
          return (
            <a
              key={index}
              href={block.textContent}
              target="_blank"
              rel="noreferrer"
            >
              {block.textContent}
            </a>
          );
        }

        if (type === 'IMAGE') {
          const attachment =
            attachmentMap.get(
              Number(block.attachmentId)
            );

          if (!attachment) return null;

          return (
            <AttachmentGallery
              key={index}
              postId={post.postId}
              attachments={[attachment]}
            />
          );
        }
        if (type === 'FILE') {
          const attachment = attachmentMap.get(Number(block.attachmentId));
          if (!attachment) return null;

          return <AttachmentGallery key={index} postId={post.postId} attachments={[attachment]} />;
        }
        return null;
      })}
      <AttachmentGallery postId={post.postId} attachments={attachmentsOutsideBlocks} />
    </>
  );
};
function PostCard({
  post,
  profileImage,
  onToggleLike,
  onOpenComment,
  onProfileImageError,
  onEditPost,
  onDeletePost,
  currentUserId
}: {
  post: PostItem;
  profileImage?: ProfileImageEntry;
  onToggleLike: (post: PostItem) => void;
  onOpenComment: (post: PostItem) => void;
  onProfileImageError: (userId: string) => void;
  onEditPost: (post: PostItem) => void;
  onDeletePost: (post: PostItem) => void;
  currentUserId: string | null;
}) {
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const userName = post.user?.name?.trim() || 'User';
  const profilePhotoId = post.user?.profilePhotoId;
  const commentCount = post.commentCount ?? post.comments?.length ?? 0;
  const hasProfilePhoto = Boolean(profilePhotoId && getValidImageId(profilePhotoId));
  const profileImageUrl = profileImage?.url;
  const postText = post.contentBlocks?.length
    ? post.contentBlocks
        .filter((block) => ['TEXT', 'LINK'].includes(block.blockType?.toUpperCase()))
        .map((block) => block.textContent || '')
        .join(' ')
    : post.content?.replace(/<[^>]*>/g, '') || '';
  const hasLongContent = postText.trim().length > 280;

  return (
    <article className={styles.feedCard}>
      <header className={styles.feedHeader}>
        <div className={styles.feedAvatar}>
          {hasProfilePhoto && profileImage?.status === 'loading' ? (
            <span className={styles.avatarSkeleton} aria-label="Loading profile image" />
          ) : hasProfilePhoto && profileImageUrl ? (
            // <img
            //   src={profileImageUrl}
            //   alt="profile"
            //   loading="lazy"
            //   onError={() => onProfileImageError(post.user.userId)}
            // />
             <PhotoImage photoId={profilePhotoId} />
          ) : (
            <span>{getInitials(userName)}</span>
          )}
        </div>
        <div>
          <h2 className={styles.feedAuthor}>{userName}</h2>
          <p className={styles.feedTimestamp}>{formatDate(post.createdAt.toLocaleString())}</p>
        </div>
        {post.user?.userId === currentUserId && (
          <div className="relative ml-auto">
            <button
              type="button"
              className={styles.postMenuButton}
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label="Post options"
              aria-expanded={isMenuOpen}
            >
              <MoreVertical className={styles.postMenuIcon} />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 top-full z-20 mt-2 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onEditPost(post);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="h-4 w-4" />
                  Edit Post
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onDeletePost(post);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Post
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <div className={styles.postContentWrapper}>
        <div
          className={`${styles.feedContent} ${hasLongContent && !isContentExpanded ? styles.feedContentCollapsed : ''}`}
        >
          {renderContentBlocks(post)}
        </div>

        {hasLongContent && (
          <button
            type="button"
            className={`${styles.expandPostButton} ${!isContentExpanded ? styles.expandPostButtonInline : ''}`}
            onClick={() => setIsContentExpanded((expanded) => !expanded)}
            aria-expanded={isContentExpanded}
          >
            {isContentExpanded ? 'Show less' : '...more'}
          </button>
        )}
      </div>

     

      <div className={styles.feedDivider} />

      <div className={styles.feedActions}>
        <LikeButton
          count={post.likeCount ?? 0}
          isLiked={Boolean(post.isLikedByCurrentUser)}
          onToggle={() => onToggleLike(post)}
        />
        <button type="button" className={styles.feedActionButton} onClick={() => onOpenComment(post)}>
          <MessageCircle className={styles.actionIcon} />
          <span>{commentCount} Comment</span>
        </button>
      </div>
    </article>
  );
}

export default function ViewPost() {
  const navigate = useNavigate();
  const currentUserPhotoId = useAppSelector((state) => state.userProfile.personalInfo?.photoId ?? null);
  const { postId: targetPostId } = useParams<{ postId: string }>();
  const [posts, setPosts] = useState<PostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FeedFilter>('recent');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isTrendsSheetOpen, setIsTrendsSheetOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState('You');
  const [commentPost, setCommentPost] = useState<PostItem | null>(null);
  const [commentText, setCommentText] = useState('');
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [postPendingDelete, setPostPendingDelete] = useState<PostItem | null>(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [profileImages, setProfileImages] = useState<ProfileImageMap>({});
  const [trendingHashtags, setTrendingHashtags] = useState<TrendingHashtag[]>([]);
  const [isTrendsLoading, setIsTrendsLoading] = useState(true);
  const feedTopRef = useRef<HTMLDivElement | null>(null);
const labels = useMenuLabels();
 const viewPostLabel = labels.viewPosts;
  const hashtagSearch = searchTerm.trim().match(/^#\S+$/)?.[0] ?? '';
  const isTextSearchActive = searchTerm.trim().length > 0 && !hashtagSearch;
  const isVisibilityFilterActive = visibilityFilter !== 'all';
  const shouldUseClientFiltering = isVisibilityFilterActive || isTextSearchActive;
  const requestStart = shouldUseClientFiltering ? 0 : pageIndex * POSTS_PAGE_SIZE;
  const requestLength = shouldUseClientFiltering ? CLIENT_FILTER_FETCH_SIZE : POSTS_PAGE_SIZE;

  useEffect(() => {
    const loadCurrentUser = async () => {
      setCurrentUserId(await getSensitiveData('userId'));
      const storedName = await getSensitiveData('userName');
      if (storedName) {
        setCurrentUserName(storedName);
      }
    };

    loadCurrentUser();
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchPosts = async () => {
      setLoading(true);

      try {
        const payload = {
          start: requestStart,
          length: requestLength,
          orderColumn: filter === 'liked' ? 'LikeCount' : 'CreatedAt',
          orderDirection: filter === 'oldest' ? 0 : 1,
          search: '',
        };
        if (hashtagSearch) {
          const res = await GetTrendingByHashtag(hashtagSearch, payload);

          if (isActive) {
            setPosts(Array.isArray(res?.data) ? res.data : []);
            setRecordsFiltered(res?.recordsFiltered ?? res?.recordsTotal ?? 0);
            setRecordsTotal(res?.recordsTotal ?? res?.recordsFiltered ?? 0);
          }
        } else {
        const res = await Admin_ViewPosts(payload);

        if (res?.success && Array.isArray(res?.data?.data)) {
          if (isActive) {
            setPosts(res.data.data);
            setRecordsFiltered(res.data.recordsFiltered ?? res.data.recordsTotal ?? 0);
            setRecordsTotal(res.data.recordsTotal ?? res.data.recordsFiltered ?? 0);
          }
          } else if (isActive) {
            setPosts([]);
            setRecordsFiltered(0);
            setRecordsTotal(0);
          }
        }
      } catch (err) {
        console.error('Error loading posts:', err);
        if (isActive) {
          setPosts([]);
          setRecordsFiltered(0);
          setRecordsTotal(0);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchPosts();

    return () => {
      isActive = false;
    };
  }, [requestStart, requestLength, filter, hashtagSearch]);

  useEffect(() => {
    const fetchTrendingHashtags = async () => {
      try {
        const res = await Admin_GetTrendingHashtags();
        setTrendingHashtags(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error('Error loading trending hashtags:', err);
        setTrendingHashtags([]);
      } finally {
        setIsTrendsLoading(false);
      }
    };

    fetchTrendingHashtags();
  }, []);

  useEffect(() => {
    if (!loading && targetPostId) {
      const el = document.getElementById(`post-${targetPostId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [loading, targetPostId]);

  useEffect(() => {
    feedTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pageIndex]);

  useEffect(() => {
    let isMounted = true;
    const usersWithPhotos = new Map<string, number>();

    posts.forEach((post) => {
      const userId = post.user?.userId;
      const profilePhotoId = getValidImageId(post.user?.profilePhotoId);

      if (userId && profilePhotoId) {
        usersWithPhotos.set(userId, profilePhotoId);
      }
    });

    usersWithPhotos.forEach((profilePhotoId, userId) => {
      const cachedUrl = profileImageUrlCache.get(userId);
      if (cachedUrl) {
        setProfileImages((current) => ({
          ...current,
          [userId]: { url: cachedUrl, status: 'loaded' },
        }));
        return;
      }

      setProfileImages((current) => {
        if (current[userId]?.status === 'loading' || current[userId]?.status === 'loaded') {
          return current;
        }

        return {
          ...current,
          [userId]: { url: null, status: 'loading' },
        };
      });

      const cachedPromise =
        profileImagePromiseCache.get(userId) ??
        fetchProfileImage(profilePhotoId)
          .then((url) => {
            profileImageUrlCache.set(userId, url);
            profileImagePromiseCache.delete(userId);
            return url;
          })
          .catch((error) => {
            profileImagePromiseCache.delete(userId);
            throw error;
          });

      if (!profileImagePromiseCache.has(userId)) {
        profileImagePromiseCache.set(userId, cachedPromise);
      }

      cachedPromise
        .then((url) => {
          if (!isMounted) return;
          setProfileImages((current) => ({
            ...current,
            [userId]: { url, status: 'loaded' },
          }));
        })
        .catch((error) => {
          console.error('Error loading profile image:', { userId, error });
          if (!isMounted) return;
          setProfileImages((current) => ({
            ...current,
            [userId]: { url: null, status: 'error' },
          }));
        });
    });

    return () => {
      isMounted = false;
    };
  }, [posts]);

  const handleToggleLike = async (post: PostItem) => {
    if (!currentUserId) return;

    const wasLiked = Boolean(post.isLikedByCurrentUser);
    const nextLiked = !wasLiked;
    const nextCount = Math.max(0, (post.likeCount ?? 0) + (nextLiked ? 1 : -1));

    setPosts((currentPosts) =>
      currentPosts.map((item) =>
        item.postId === post.postId
          ? { ...item, isLikedByCurrentUser: nextLiked, likeCount: nextCount }
          : item
      )
    );

    try {
      await Admin_TogglePostLike({
        postId: post.postId,
        userId: currentUserId,
        reactionTypeId: 1,
      });
    } catch (error) {
      console.error('Error toggling post like:', error);
      setPosts((currentPosts) =>
        currentPosts.map((item) =>
          item.postId === post.postId
            ? { ...item, isLikedByCurrentUser: wasLiked, likeCount: post.likeCount ?? 0 }
            : item
        )
      );
    }
  };

  const handleOpenComment = (post: PostItem) => {
    setCommentPost(post);
    setCommentText('');
  };

  const handleEditPost = async (post: PostItem) => {
    try {
      const updatedAttachments = await Promise.all(
        (post.attachments || []).map(async (att) => {
          if (
            att.fileType?.toLowerCase().includes('image') &&
            att.attachmentId
          ) {
            const res = await Admin_GetPostAttachmentSasUrl(
              Number(att.attachmentId)
            );

              const fileUrl =
                extractSasUrl(res?.data?.data) ??
                extractSasUrl(res?.data?.fileUrl);

            return {
              ...att,
              fileUrl: fileUrl ?? null,
            };
          }

          return att;
        })
      );

      navigate(`/edit-post/${post.postId}`, {
        state: {
          post: {
            ...post,
            attachments: updatedAttachments,
          },
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePost = (post: PostItem) => {
    setPostPendingDelete(post);
  };

  const handleConfirmDeletePost = async () => {
    if (!postPendingDelete) return;

    try {
      setIsDeletingPost(true);
      const response = await Admin_DeletePost(postPendingDelete.postId);
      if (response?.success === false) {
        throw new Error(response.message || 'Unable to delete post.');
      }
      setPosts((currentPosts) => currentPosts.filter((item) => item.postId !== postPendingDelete.postId));
      setPostPendingDelete(null);
      setToast({ message: 'Post deleted successfully.', type: 'success' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete post. Please try again.';
      setToast({ message, type: 'error' });
    } finally {
      setIsDeletingPost(false);
    }
  };

  const handleProfileImageError = (userId: string) => {
    profileImageUrlCache.delete(userId);
    profileImagePromiseCache.delete(userId);
    setProfileImages((current) => ({
      ...current,
      [userId]: { url: null, status: 'error' },
    }));
  };

  const handleCloseComment = () => {
    if (isCommentSubmitting) return;
    setCommentPost(null);
    setCommentText('');
  };

  const handleSubmitComment = async () => {
    if (!currentUserId || !commentPost || !commentText.trim()) return;

    setIsCommentSubmitting(true);
    const trimmedText = commentText.trim();

    try {
      await Admin_PostComment({
        commentId: 0,
        userId: currentUserId,
        postId: commentPost.postId,
        commentText: trimmedText,
      });

      const newComment = {
        commentId: Date.now(),
        commentText: trimmedText,
        createdAt: new Date().toISOString(),
        user: {
          name: currentUserName || 'You',
          specialtyName: '',
          profilePhotoId: currentUserPhotoId,
        },
      };

      setPosts((currentPosts) =>
        currentPosts.map((item) =>
          item.postId === commentPost.postId
            ? {
              ...item,
              commentCount: (item.commentCount ?? 0) + 1,
              comments: [...(item.comments ?? []), newComment],
            }
            : item
        )
      );

      setCommentPost((current) =>
        current
          ? {
            ...current,
            commentCount: (current.commentCount ?? 0) + 1,
            comments: [...(current.comments ?? []), newComment],
          }
          : current
      );
      setCommentText('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const visiblePosts = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return posts
      .filter((post) => {
        if (visibilityFilter === 'public') return post.visibilityId === 1;
        if (visibilityFilter === 'network') return post.visibilityId === 2;
        if (visibilityFilter === 'onlyMe') return post.visibilityId !== 1 && post.visibilityId !== 2;
        return true;
      })
      .filter((post) => {
        if (!search) return true;
        const plainContent = post.content?.replace(/<[^>]*>/g, '').toLowerCase() || '';
        return (
          plainContent.includes(search) ||
          post.user?.name?.toLowerCase().includes(search) ||
          post.hashtags?.some((tag) => tag.toLowerCase().includes(search))
        );
      })
      .sort((first, second) => {
        const firstTime = new Date(first.createdAt).getTime();
        const secondTime = new Date(second.createdAt).getTime();
        if (filter === 'oldest') return firstTime - secondTime;
        if (filter === 'liked') return (second.likeCount ?? 0) - (first.likeCount ?? 0);
        return secondTime - firstTime;
      });
  }, [posts, searchTerm, filter, visibilityFilter]);

  const displayedPosts = shouldUseClientFiltering
    ? visiblePosts.slice(pageIndex * POSTS_PAGE_SIZE, (pageIndex + 1) * POSTS_PAGE_SIZE)
    : visiblePosts;

  const paginationRecordCount = shouldUseClientFiltering
    ? visiblePosts.length
    : recordsFiltered;
  const pageStart = displayedPosts.length === 0 ? 0 : pageIndex * POSTS_PAGE_SIZE + 1;
  const pageEnd = pageIndex * POSTS_PAGE_SIZE + displayedPosts.length;
  const hasPreviousPage = pageIndex > 0;
  // The posts API currently reports the count of the current page as its total.
  // A full page is therefore the reliable signal that another request may return posts.
  const hasNextPage = shouldUseClientFiltering
    ? pageEnd < visiblePosts.length
    : posts.length === POSTS_PAGE_SIZE;

  const handleFilterChange = (nextFilter: FeedFilter) => {
    setFilter(nextFilter);
    setPageIndex(0);
  };

  const handleVisibilityFilterChange = (nextVisibilityFilter: VisibilityFilter) => {
    setVisibilityFilter(nextVisibilityFilter);
    setPageIndex(0);
  };

   const handleSelectHashtag = (tag: string) => {
    setSearchTerm(tag);
    setPageIndex(0);
  };

  return (
    <DashboardLayout title={viewPostLabel}>
      <div className={styles.viewColumns}>
        <main className={styles.viewMainColumn}>
            <div ref={feedTopRef} />
            <Header />

            <div className={styles.feedToolbar}>
              <label className={styles.feedSearch}>
                <Search className={styles.actionIcon} />
                <input
                  type="search"
                  placeholder="Search posts"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setPageIndex(0);
                  }}
                />
              </label>

              <VisibilityDropdown
                value={visibilityFilter}
                onChange={handleVisibilityFilterChange}
              />

              <FilterDropdown value={filter} onChange={handleFilterChange} />

              <button
                type="button"
                className={styles.mobileToolbarButton}
                onClick={() => setIsFilterSheetOpen(true)}
                aria-label="Open filters"
              >
                <SlidersHorizontal className={styles.actionIcon} />
                <span>Filter</span>
              </button>

              <button
                type="button"
                className={styles.mobileToolbarButton}
                onClick={() => setIsTrendsSheetOpen(true)}
                aria-label="Open trends"
              >
                <TrendingUp className={styles.actionIcon} />
                <span>Trends</span>
              </button>
            </div>

            <section className={styles.feedList} aria-live="polite">
              {loading ? (
                <div className={styles.emptyFeed}>Loading posts...</div>
              ) : displayedPosts.length === 0 ? (
                <div className={styles.emptyFeed}>No posts found</div>
              ) : (
                displayedPosts.map((post) => (
                  <div key={post.postId} id={`post-${post.postId}`}>
                    <PostCard
                      post={post}
                      profileImage={profileImages[post.user?.userId]}
                      onToggleLike={handleToggleLike}
                      onOpenComment={handleOpenComment}
                      onEditPost={handleEditPost}
                      onDeletePost={handleDeletePost}
                      onProfileImageError={handleProfileImageError}
                      currentUserId={currentUserId}
                    />
                  </div>
                ))
              )}
            </section>

            {!loading && paginationRecordCount > 0 && (
              <nav className={styles.feedPagination} aria-label="Posts pagination">
                <p className={styles.paginationSummary}>
                  Showing {pageStart}-{pageEnd} of {paginationRecordCount} posts
                  {!isVisibilityFilterActive && recordsTotal !== recordsFiltered ? ` (${recordsTotal} total)` : ''}
                </p>
                <div className={styles.paginationActions}>
                  <button
                    type="button"
                    className={styles.paginationButton}
                    disabled={!hasPreviousPage}
                    onClick={() => setPageIndex((current) => Math.max(current - 1, 0))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className={styles.paginationButton}
                    disabled={!hasNextPage}
                    onClick={() => setPageIndex((current) => current + 1)}
                  >
                    Next
                  </button>
                </div>
              </nav>
            )}
        </main>

        <TrendsPanel
          hashtags={trendingHashtags}
          isLoading={isTrendsLoading}
          onSelectHashtag={handleSelectHashtag}
         />
      </div>

      <BottomSheet
        title="Filter Posts"
        isOpen={isFilterSheetOpen}
        onClose={() => setIsFilterSheetOpen(false)}
      >
        <VisibilityDropdown
          value={visibilityFilter}
          onChange={handleVisibilityFilterChange}
          compact
        />
        <FilterDropdown
          value={filter}
          onChange={handleFilterChange}
          onAfterChange={() => setIsFilterSheetOpen(false)}
          compact
        />
      </BottomSheet>

      <BottomSheet
        title="Trending"
        isOpen={isTrendsSheetOpen}
        onClose={() => setIsTrendsSheetOpen(false)}
      >
        <TrendsPanel
          inSheet
          hashtags={trendingHashtags}
          isLoading={isTrendsLoading}
          onSelectHashtag={(tag) => {
            handleSelectHashtag(tag);
            setIsTrendsSheetOpen(false);
          }}
        />
      </BottomSheet>

      <CommentModal
        isOpen={Boolean(commentPost)}
        postId={commentPost?.postId}
        currentUserName={currentUserName}
        isSubmitting={isCommentSubmitting}
        value={commentText}
        onChange={setCommentText}
        onClose={handleCloseComment}
        onSubmit={handleSubmitComment}
      />
      {postPendingDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 px-4 py-6">
          <div className="w-full max-w-md rounded-2xl bg-white px-6 py-7 shadow-2xl sm:px-8">
            <h2 className="text-lg font-bold text-gray-900">Delete this post?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-500">
              Are you sure you want to delete this post? This action cannot be undone.
            </p>
            <div className="mt-7 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPostPendingDelete(null)}
                disabled={isDeletingPost}
                className="rounded-lg bg-slate-100 px-5 py-2.5 text-sm font-semibold text-gray-800 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeletePost}
                disabled={isDeletingPost}
                className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeletingPost ? 'Deleting...' : 'Delete Post'}
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </DashboardLayout>
  );
}
